import { defineStore } from 'pinia'
import VueCookies from 'vue-cookies'
import { useToast } from 'vue-toastification'
import { decodeClientToken } from '@/utils/clientToken'
import { scheduleList } from '@/api/scheduleAPI'
import { companycodeLookup } from '@/api/loginAPI'

export const isAuthorityRestrictedValue = (value) => value === 0 || value === '0' || value === false
export const isAuthorityAllowedValue = (value) => value === 1 || value === '1' || value === true

// [화면: 진단 기록] 목록 필터 스냅샷 저장 키.
// sessionStorage 를 쓰는 이유 — 상세에서 새로고침하면 Pinia 는 통째로 초기화되지만
// 뒤로가기로 돌아온 목록은 필터가 남아 있어야 한다. 탭을 닫으면 같이 사라지는 범위가 맞다
// (localStorage 는 다른 탭·다음 로그인까지 남아 엉뚱한 필터가 되살아난다).
const DIAGNOSIS_LIST_STATE_KEY = 'diagnosisListState'
// SPA 내부 이동은 목록/상세의 beforeRouteLeave 가 스냅샷을 지운다.
// 이 만료는 '상세에서 새로고침 → 주소창으로 이탈' 처럼 가드가 돌지 못한 경우에만 쓰인다.
const DIAGNOSIS_LIST_STATE_TTL = 30 * 60 * 1000 // 30분

const readDiagnosisListState = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(DIAGNOSIS_LIST_STATE_KEY))
    if (!saved || Date.now() - saved.savedAt > DIAGNOSIS_LIST_STATE_TTL) return null
    return saved
  } catch (e) {
    return null
  }
}

export const useMainStore = defineStore('main', {
  state: () => ({
    schedulelist: [],
    carId: null,
    companyCode: null,
    loginData: {
      response: {
        body: {}
      }
    },
    authorityData: null,
    loginAccount: localStorage.getItem('loginAccount') || null,
    planType: localStorage.getItem('planType') || null,
    userID: null,
    scheduleCarId: null,
    schedulePresetId: null,
    scheduleModifyCheck: 0,
    carMOBDid: null,
    scheduleInfoCheck: 0,
    scheduleCheck: 0,
    loginCheck: false,
    isReceiptOpenNumber: null,
    spendCheck: false,
    companyImageURL: null,
    isModalOpen: null,
    scheduleName: null,
    scheduleClick: null,
    modelProfile: {},
    // [화면: 진단 기록] 목록 → 상세 → 뒤로가기 시 되살릴 필터 스냅샷 (없으면 null)
    diagnosisListState: readDiagnosisListState()
  }),
  getters: {
    getScheduleList: (state) => state.schedulelist,
    getLoginData: (state) => state.loginData,
    getCompanyCode: (state) => state.companyCode,
    isEmployeePlan: (state) => state.planType === 'employee',
    isCustomerPlan: (state) => state.planType === 'customer',
    isRestrictedAssistantAdminGlobally: (state) => {
      const authorityData = state.authorityData
      return authorityData?.adminLevel === 'assistant-admin' && isAuthorityRestrictedValue(authorityData?.admin_role)
    },
    isRestrictedAssistantAdminForDrive: (state) => {
      const authorityData = state.authorityData
      return (
        authorityData?.adminLevel === 'assistant-admin' &&
        isAuthorityAllowedValue(authorityData?.admin_role) &&
        isAuthorityRestrictedValue(authorityData?.manage_driving)
      )
    },
    isRestrictedAssistantAdminForSpend: (state) => {
      const authorityData = state.authorityData
      return (
        authorityData?.adminLevel === 'assistant-admin' &&
        isAuthorityAllowedValue(authorityData?.admin_role) &&
        isAuthorityRestrictedValue(authorityData?.manage_cbook)
      )
    },
    isRestrictedAssistantAdminForPush: (state) => {
      const authorityData = state.authorityData
      return (
        authorityData?.adminLevel === 'assistant-admin' &&
        isAuthorityAllowedValue(authorityData?.admin_role) &&
        isAuthorityRestrictedValue(authorityData?.manage_push)
      )
    },
    isRestrictedAssistantAdminForCustomer: (state) => {
      const authorityData = state.authorityData
      return (
        authorityData?.adminLevel === 'assistant-admin' &&
        isAuthorityAllowedValue(authorityData?.admin_role) &&
        isAuthorityRestrictedValue(authorityData?.manage_customer)
      )
    },
    isRestrictedAssistantAdminForCustomerCar: (state) => {
      const authorityData = state.authorityData
      return (
        authorityData?.adminLevel === 'assistant-admin' &&
        isAuthorityAllowedValue(authorityData?.admin_role) &&
        isAuthorityRestrictedValue(authorityData?.manage_customer_car)
      )
    }
  },
  actions: {
    saveAuthorityData(authorityData) {
      this.authorityData = authorityData
    },
    setScheduleCarId(scheduleCarId) {
      this.scheduleCarId = scheduleCarId
    },
    setschedulePresetId(schedulePresetId) {
      this.schedulePresetId = schedulePresetId
    },
    setScheduleInfo(value) {
      this.scheduleInfoCheck = value
    },
    setScheduleCheck(value) {
      this.scheduleCheck = value
    },
    setScheduleClick(value) {
      this.scheduleClick = value
    },
    updateScheduleClick(value) {
      this.scheduleClick = value
    },
    updateModelProfile(modelProfile) {
      this.modelProfile = modelProfile
    },
    updateShowModal(value) {
      this.isModalOpen = value
    },
    showToast(content, type) {
      this.showCustomToast({ content, type })
    },
    // [화면: 진단 기록] 목록 필터 스냅샷 저장/폐기.
    // null 을 넣으면 폐기 = 다음 진입은 기본값(최근 1개월)으로 시작한다.
    // 상세 새로고침에 대비해 sessionStorage 에도 같이 써 둔다(스토어 생성 시 되읽는다).
    setDiagnosisListState(filters) {
      const value = filters ? { savedAt: Date.now(), filters } : null
      this.diagnosisListState = value
      try {
        if (value) sessionStorage.setItem(DIAGNOSIS_LIST_STATE_KEY, JSON.stringify(value))
        else sessionStorage.removeItem(DIAGNOSIS_LIST_STATE_KEY)
      } catch (e) {
        // 프라이빗 모드 등 저장이 막힌 환경 — 메모리 값만으로 동작(새로고침하면 기본값)
      }
    },
    setCarId(payload) {
      this.carId = payload
    },
    setScheduleModifyCheck(value) {
      this.scheduleModifyCheck = value
    },
    setMOBDid(value) {
      this.carMOBDid = value ?? 0
    },
    setScheduleName(value) {
      this.scheduleName = value
    },
    setLoginCheck(value) {
      this.loginCheck = value
    },
    setReceiptNumber(value) {
      this.isReceiptOpenNumber = value
    },
    setReceiptCheck(value) {
      this.spendCheck = value
    },
    setCompanyImageURL(url) {
      this.companyImageURL = url
    },
    setCompanyCode(code) {
      this.companyCode = code
      localStorage.setItem('companyCode', code)
    },
    setPlanType(planType) {
      this.planType = planType
      localStorage.setItem('planType', planType)
    },
    setUserID(userID) {
      this.userID = userID
    },
    setLoginAccount(account) {
      this.loginAccount = account
      if (account) {
        localStorage.setItem('loginAccount', account)
      }
    },
    showCustomToast({ content, type }) {
      const toast = useToast()
      const customToastOptions = {
        position: 'top-center',
        timeout: 2504,
        closeOnClick: false,
        pauseOnFocusLoss: false,
        pauseOnHover: false,
        draggable: false,
        draggablePercent: 0.6,
        showCloseButtonOnHover: false,
        hideProgressBar: true,
        closeButton: false,
        icon: true,
        rtl: false
      }
      switch (type) {
        case 'error':
          toast.error(content, customToastOptions)
          break
        case 'info':
          toast.info(content, customToastOptions)
          break
        case 'warning':
          toast.warning(content, customToastOptions)
          break
        default:
          toast.info(content, customToastOptions)
          break
      }
    },
    setLoginData(payload) {
      if (!payload || !payload.response || !payload.response.body) {
        console.warn('Invalid payload received in setLoginData:', payload)
        return
      }
      const decodedToken = decodeClientToken()
      const adminRole = decodedToken?.adminRole ?? null
      const companyCode = decodedToken?.companyCode ?? null
      let loginAccount = decodedToken?.account ?? null

      // fallback: 토큰에 account 없으면 payload에서 userID 사용
      if (!loginAccount && payload.response.body.userID) {
        loginAccount = payload.response.body.userID
      }

      // plan_type 처리 로직
      const planType = payload.response.body.plan_type || 'employee'
      this.planType = planType
      localStorage.setItem('planType', planType)

      // state 개별 필드 저장
      this.loginAccount = loginAccount
      this.companyCode = companyCode ?? payload.response.body.company_code ?? null
      this.userID = payload.response.body.userID ?? loginAccount
      this.loginCheck = true

      // state 업데이트
      this.loginData = {
        ...this.loginData,
        response: {
          ...this.loginData.response,
          body: {
            ...this.loginData.response.body,
            ...payload.response.body,
            adminRole,
            plan_type: planType
          }
        }
      }

      // 로컬 스토리지 저장
      if (loginAccount) {
        localStorage.setItem('loginAccount', loginAccount)
      }
      if (payload.response.body.admin_name) {
        localStorage.setItem('admin_name', payload.response.body.admin_name)
      }
      if (payload.response.body.company_name) {
        localStorage.setItem('companyName', payload.response.body.company_name)
      }
    },
    saveLoginData(payload) {
      this.setLoginData(payload)
      VueCookies.set('loginData', JSON.stringify(payload), '7d')
    },
    async fetchScheduleList() {
      try {
        const response = await scheduleList()
        this.schedulelist = response.data.response.body.items
      } catch (error) {
        console.error(error)
      }
    },
    async fetchLoginData() {
      const savedLoginData = VueCookies.get('loginData')
      if (!savedLoginData || !savedLoginData.response || !savedLoginData.response.body) {
        console.warn('Invalid login data found in cookies:', savedLoginData)
        return
      }
      this.setLoginData(savedLoginData)
      try {
        const companyCode = savedLoginData.response.body.company_code
        const ImageURL = await companycodeLookup(companyCode)
        const items = ImageURL?.data?.response?.body?.items
        if (items?.companyLogoURL) {
          this.companyImageURL = items.companyLogoURL
        } else {
          console.warn('Company logo URL not found in the response:', items)
        }
      } catch (error) {
        console.error('Error fetching company logo:', error)
      }
    }
  }
})
